import React from "react";
import classNames from "classnames";
import { PERIOD_LABELS, PeriodKey } from "./finance-data";

// Page frame shared by the Finances screens: sticky title bar + content.
export function FinPage({ title, subtitle, actions, children }: { title: string; subtitle: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="p-6 lg:p-7 min-h-full bg-[#fbf8f4] text-[#1f1b2e] overflow-clip">
      <div className="sticky top-[50px] z-30 -mx-6 px-6 lg:-mx-7 lg:px-7 -mt-6 lg:-mt-7 pt-6 lg:pt-7 pb-3 mb-4 bg-[#fbf8f4] flex items-start justify-between gap-x-6 gap-y-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-xs text-[#6b6480] mt-1 font-medium">{subtitle}</p>
        </div>
        {actions && <div className="flex items-center gap-2.5 flex-wrap">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Kpi({ label, value, sub, tone = "#1f1b2e", onClick }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string; onClick?: () => void }) {
  return (
    <div onClick={onClick} className={classNames("p-3.5 bg-white border border-[#efe7dc] rounded-xl", onClick && "cursor-pointer hover:border-amber-300")}>
      <div className="text-[10px] font-bold tracking-wider uppercase text-[#8a7b6b]">{label}</div>
      <div className="text-xl font-bold mt-1" style={{ color: tone }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#a0927f] mt-0.5">{sub}</div>}
    </div>
  );
}

export function Card({ title, right, children, className }: { title?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={classNames("bg-white border border-[#efe7dc] rounded-xl", className)}>
      {(title || right) && (
        <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
          <div className="text-sm font-bold">{title}</div>
          <div className="ml-auto">{right}</div>
        </div>
      )}
      {children}
    </div>
  );
}

export function PeriodPicker({
  value,
  onChange,
  from,
  to,
  onFrom,
  onTo,
}: {
  value: PeriodKey;
  onChange: (p: PeriodKey) => void;
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  const cls = "h-9 px-2.5 border border-[#e7dccd] rounded-lg bg-white text-xs";
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select value={value} onChange={(e) => onChange(e.target.value as PeriodKey)} className={cls}>
        {(Object.keys(PERIOD_LABELS) as PeriodKey[]).map((k) => (
          <option key={k} value={k}>
            {PERIOD_LABELS[k]}
          </option>
        ))}
      </select>
      {value === "custom" && (
        <>
          <input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className={cls} />
          <span className="text-gray-400">→</span>
          <input type="date" value={to} onChange={(e) => onTo(e.target.value)} className={cls} />
        </>
      )}
    </div>
  );
}

export const btnPrimary = "h-9 px-4 bg-[#92400e] hover:bg-[#78350f] rounded-lg text-xs font-bold text-white whitespace-nowrap disabled:opacity-40";
export const btnGhost = "h-9 px-3.5 border border-[#e7dccd] bg-white hover:bg-[#fdf8f1] rounded-lg text-xs font-semibold text-[#5b4636] whitespace-nowrap disabled:opacity-40";

export const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  Réussie: { bg: "#d1fae5", fg: "#047857" },
  "En attente": { bg: "#fef3c7", fg: "#b45309" },
  Échouée: { bg: "#fee2e2", fg: "#b91c1c" },
};

export function Toast({ msg }: { msg: string | null }) {
  return msg ? <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-[95] px-4 py-2.5 bg-[#1f1b2e] rounded-full text-xs font-semibold text-white shadow-xl">{msg}</div> : null;
}
